import { useEffect, useRef, useState } from 'react';

import { Alert, Radio, Table } from 'antd';
import { T } from 'react-translator-component';

import {
  formatDateTime,
  formatKwh,
  formatMoney,
  maskTag,
  SESSION_FILTERS,
  SESSION_STATUS,
  SessionFilter,
  SESSIONS_PAGE_SIZE,
  sessionsPageQuery,
  statusOf,
} from './ocpiHelpers';
import type { Navigate } from './OcpiPage';
import StatusTag from './StatusTag';
import * as S from './styled';

import { getOcpiSessions } from '../../../hooks/ocpi';
import { OcpiLocation, OcpiPage, OcpiSession } from '../../../types/responses/Ocpi';

interface Props {
  range: { dateFrom: string; dateTo: string };
  byUuid: Map<string | undefined, OcpiLocation>;
  nav: Navigate;
}

const EMPTY: OcpiPage<OcpiSession> = { list: [], total: 0, lastPage: true };

/**
 * Cada página e cada filtro consultam o CS com offset/limit e status (MV2-2120). O CS ordena por
 * transaction_pk decrescente, que é a ordem de início. As contagens dos filtros vêm do `total`
 * de uma consulta de 1 item por status.
 */
const SessionsTab = ({ range, byUuid, nav }: Props) => {
  const [status, setStatus] = useState<SessionFilter>('all');
  const [page, setPage] = useState(1);
  const [sessions, setSessions] = useState<OcpiPage<OcpiSession>>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<Partial<Record<SessionFilter, number>>>({});
  const request = useRef(0);

  useEffect(() => {
    let active = true;
    setCounts({});
    Promise.all(
      SESSION_FILTERS.map(async (key) => {
        const result = await getOcpiSessions({ ...sessionsPageQuery(range, key, 1), limit: 1 });
        return [key, result.error ? undefined : result.total] as const;
      }),
    ).then((entries) => active && setCounts(Object.fromEntries(entries)));
    return () => {
      active = false;
    };
  }, [range]);

  useEffect(() => {
    // Descarta a resposta de uma página que o usuário já deixou para trás.
    const id = ++request.current;
    setLoading(true);
    getOcpiSessions(sessionsPageQuery(range, status, page)).then((result) => {
      if (id !== request.current) return;
      setSessions(result);
      setLoading(false);
    });
  }, [range, status, page]);

  const changeStatus = (next: SessionFilter) => {
    setStatus(next);
    setPage(1);
  };

  return (
    <>
      <Alert
        type="info"
        style={{ marginBottom: 12 }}
        message={T('All charges made at the stations published via OCPI, by partner users or not. The partner of each charge is not identified.')}
      />
      {sessions.error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={T('Could not load the OCPI data. Try again in a few minutes.')}
        />
      )}
      <S.Toolbar>
        <Radio.Group value={status} onChange={(e) => changeStatus(e.target.value)}>
          {SESSION_FILTERS.map((key) => (
            <Radio.Button key={key} value={key}>
              {key === 'all' ? T('All') : T(statusOf(SESSION_STATUS, key).label)}
              {counts[key] !== undefined && ` · ${counts[key]}`}
            </Radio.Button>
          ))}
        </Radio.Group>
      </S.Toolbar>
      <S.Panel>
        <Table
          rowKey="transactionPk"
          dataSource={sessions.list}
          loading={loading}
          pagination={{
            current: page,
            pageSize: SESSIONS_PAGE_SIZE,
            total: sessions.total,
            showSizeChanger: false,
            onChange: setPage,
          }}
          onRow={(record) => ({
            onClick: () => nav.open({ kind: 'session', pk: record.transactionPk, session: record }),
          })}
          locale={{ emptyText: T('No charges in the period.') }}
          columns={[
            {
              title: T('Charge'),
              render: (_, s) => (
                <>
                  <S.Mono>#{s.transactionPk}</S.Mono>
                  <div>
                    <S.Muted>
                      {T('Tag')} {maskTag(s.idTag)}
                    </S.Muted>
                  </div>
                </>
              ),
            },
            { title: T('Status'), dataIndex: 'status', render: (s) => <StatusTag {...statusOf(SESSION_STATUS, s)} /> },
            {
              title: T('Station'),
              render: (_, s) => (
                <>
                  <div>{byUuid.get(s.chargeBoxUuid)?.chargeBoxId || s.chargeBoxUuid || '—'}</div>
                  <S.Muted>
                    {T('Connector')} {s.connectorId ?? '—'}
                  </S.Muted>
                </>
              ),
            },
            {
              title: T('Start → end'),
              render: (_, s) => (
                <>
                  <div>{formatDateTime(s.startTimestamp)}</div>
                  <S.Muted>{s.stopTimestamp ? formatDateTime(s.stopTimestamp) : T('ongoing')}</S.Muted>
                </>
              ),
            },
            { title: T('Energy'), dataIndex: 'kwh', align: 'right', render: formatKwh },
            { title: T('Amount'), align: 'right', render: (_, s) => formatMoney(s.income, s.currency) },
          ]}
        />
      </S.Panel>
    </>
  );
};

export default SessionsTab;
